// frontend/src/pages/admin/UnbounceLeads.jsx
// Last Updated: April 8, 2025
// Purpose: Display and manage Unbounce leads

import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { fetchUnbounceLeads, syncUnbounceLeads } from '../../api/unbounceApi';
import LeadTable from '../../components/LeadTable';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/Card';
import { Button } from '../../components/ui/Button';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Alert, AlertDescription, AlertTitle } from '../../components/ui/alert';

const UnbounceLeads = () => {
  const navigate = useNavigate();
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [syncingUnbounce, setSyncingUnbounce] = useState(false);
  const [lastSyncTime, setLastSyncTime] = useState(null);
  const [autoRefresh, setAutoRefresh] = useState(true);
  const [retryCount, setRetryCount] = useState(0);
  const [retryAfter, setRetryAfter] = useState(0);
  const [retryTimer, setRetryTimer] = useState(null);

  // Define fetchUnbounceLeads using useCallback to prevent infinite loops
  const fetchUnbounceLeadsData = useCallback(async () => {
    setLoading(true);
    try {
      console.log('Fetching Unbounce leads...');
      const result = await fetchUnbounceLeads();
      console.log('Unbounce leads API response:', result);
      
      if (result.success) {
        if (result.data.leads) {
          setLeads(result.data.leads || []);
        } else {
          setLeads(result.data || []);
        }
        
        // Get last sync time if available
        if (result.data.lastSyncTime) {
          setLastSyncTime(result.data.lastSyncTime);
        }
        
        setError(''); // Clear any previous errors
        setRetryCount(0); // Reset retry count on success
        setRetryAfter(0); // Reset retry after on success
      } else {
        console.error('Failed to load Unbounce leads:', result.message);
        
        // Handle rate limiting
        if (result.status === 429) {
          setError(`Rate limit exceeded. Retrying in ${result.retryAfter} seconds...`);
          setRetryAfter(result.retryAfter);
          
          // Clear existing timer
          if (retryTimer) {
            clearTimeout(retryTimer);
          }
          
          // Set up retry timer
          const timer = setTimeout(() => {
            setRetryCount(prev => prev + 1);
            setRetryAfter(0);
          }, result.retryAfter * 1000);
          
          setRetryTimer(timer);
        } else {
          setError(result.message || 'Failed to load Unbounce leads');
        }
      }
    } catch (err) {
      console.error('Error fetching Unbounce leads:', err);
      setError('An error occurred while fetching Unbounce leads');
    } finally {
      setLoading(false);
    }
  }, [retryTimer]);

  // Add fetchUnbounceLeads to the dependency array
  useEffect(() => {
    let interval;
    
    if (autoRefresh) {
      interval = setInterval(() => {
        fetchUnbounceLeadsData();
      }, 5 * 60 * 1000); // 5 minutes
    }
    
    return () => {
      if (interval) {
        clearInterval(interval);
      }
    };
  }, [autoRefresh, fetchUnbounceLeadsData]);

  // Add fetchUnbounceLeads and retryTimer to the dependency array
  useEffect(() => {
    if (retryAfter === 0) {
      fetchUnbounceLeadsData();
    }
    
    return () => {
      if (retryTimer) {
        clearTimeout(retryTimer);
      }
    };
  }, [retryCount, retryAfter, fetchUnbounceLeadsData, retryTimer]);

  const handleSyncUnbounce = async () => {
    try {
      setError('');
      setSuccess('');
      setSyncingUnbounce(true);
      
      console.log('Starting Unbounce sync...');
      const result = await syncUnbounceLeads();
      console.log('Sync response:', result);
      
      if (result.success) {
        setSuccess('Unbounce leads synchronized successfully. ' + (result.message || ''));
        await fetchUnbounceLeadsData();
        if (result.syncedAt) {
          setLastSyncTime(result.syncedAt);
        }
      } else {
        // Handle rate limiting
        if (result.status === 429) {
          setError(`Rate limit exceeded. Please try again in ${result.retryAfter} seconds.`);
        } else {
          setError(result.message || 'Failed to synchronize Unbounce leads');
        }
      }
    } catch (err) {
      console.error('Error synchronizing Unbounce leads:', err);
      setError('Failed to synchronize Unbounce leads. Please try again.');
    } finally {
      setSyncingUnbounce(false);
    }
  };

  return (
    <div className="p-4 sm:p-6">
      <div className="flex justify-between mb-4">
        <h1 className="text-xl sm:text-2xl font-semibold text-gray-800">Unbounce Leads</h1>
        <div className="flex gap-2">
          <button 
            onClick={() => setAutoRefresh(!autoRefresh)}
            className={`btn ${autoRefresh ? 'btn-primary' : 'btn-secondary'} text-sm sm:text-base`}
          >
            {autoRefresh ? 'Auto-refresh On' : 'Auto-refresh Off'}
          </button>
          <button 
            onClick={handleSyncUnbounce}
            disabled={syncingUnbounce}
            className={`btn ${syncingUnbounce ? 'btn-secondary' : 'btn-primary'} text-sm sm:text-base`}
          >
            {syncingUnbounce ? 'Syncing...' : 'Sync from Unbounce'}
          </button>
        </div>
      </div>
      
      {error && (
        <div className="mb-4 p-4 bg-red-100 text-red-700 rounded-md">
          {error}
        </div>
      )}
      
      {success && (
        <div className="mb-4 p-4 bg-green-100 text-green-700 rounded-md">
          {success}
        </div>
      )}
      
      {lastSyncTime && (
        <div className="mb-4 text-sm text-gray-500">
          Last synced: {new Date(lastSyncTime).toLocaleString()}
        </div>
      )}
      
      {loading ? (
        <div className="flex justify-center items-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
          <p className="mt-2 text-gray-500">Loading Unbounce leads...</p>
        </div>
      ) : (
        <LeadTable 
          leads={leads} 
          onEdit={null} // Option to disable edit for Unbounce leads
          onDelete={null} // Option to disable delete for Unbounce leads
        />
      )}
    </div>
  );
};

export default UnbounceLeads;